import { useRef } from 'react';

import { Roles } from 'types';
import { useAppSelector } from 'store';
import { Flex } from 'components/flex';
import { Row, Col } from 'components/grid';
import { Switch } from 'components/switch';
import { useAlert, handleMutation } from 'hooks';
import { selectAuthUserRole } from 'store/slices/auth';
import { SectionHeader } from 'components/section-header';
import {
  useTimeOffSettingsQuery,
  useSetTimeOffEnabledMutation,
} from 'store/api/time-off';

import * as Styles from './styles';

/**
 * TIME OFF (owner, Dev pass 24 Oct 2026: "needs to be in company settings
 * not in notifications"): the on/off switch for time off requests. Company
 * Owner only; it saves at once (its own endpoint), apart from the form's
 * Save. The alert's channels stay in Company Settings > Notifications.
 */
export const TimeOffSection = () => {
  const role = useAppSelector(selectAuthUserRole);
  const isOwner = role === Roles.COMPANY_OWNER;
  const { showSuccessAlert } = useAlert();
  const { data, isFetching } = useTimeOffSettingsQuery(undefined, {
    skip: !isOwner,
    refetchOnMountOrArgChange: true,
  });
  const [setEnabled, mutation] = useSetTimeOffEnabledMutation();
  handleMutation({ ...mutation });
  // One save at a time: a second click before the first answer would race
  // it (review 24 Oct). Held until the PUT answers; the re-read that
  // follows keeps the switch disabled (isFetching).
  const saving = useRef(false);

  if (!isOwner) {
    return null;
  }

  const isOn = Boolean(data?.enabled);

  return (
    <>
      <SectionHeader withBorder={false} title="Time off requests" />
      <Row>
        <Col col={4}>
          <Flex gap={8} flexDirection="column">
            <Switch
              checked={isOn}
              id="timeOffEnabled"
              name="timeOffEnabled"
              label={isOn ? 'Time off requests on' : 'Time off requests off'}
              disabled={
                !data || isFetching || mutation.isLoading || saving.current
              }
              onChange={async (event) => {
                if (saving.current) {
                  return;
                }
                saving.current = true;
                try {
                  const enabled = (event.target as HTMLInputElement).checked;
                  const result = await setEnabled(enabled);
                  if ('data' in result) {
                    showSuccessAlert(
                      enabled
                        ? 'Time off requests turned on'
                        : 'Time off requests turned off'
                    );
                  }
                } finally {
                  saving.current = false;
                }
              }}
            />
            <Styles.SwitchSubtitle>
              Reps ask for days off from their profile. The Company Owner,
              Administrative and Admin Reps decide on the Time off page. Saved
              at once.
            </Styles.SwitchSubtitle>
          </Flex>
        </Col>
      </Row>
    </>
  );
};
